/**
 * El Workspace: qué sabe el agente, qué espera aprobación y qué hay anotado.
 *
 * **No reimplementa nada.** La API es la única implementación (R8); esto sólo
 * la llama. Si algo hay que decidir aquí —qué se ve, en qué orden, qué pasa si
 * falta— es que está decidido en el sitio equivocado.
 */

import { ragApi } from './ApiService'
import { unwrap } from './envelope'

// La palabra que resuelve el espacio de la cuenta sin tener que listarlo. No se
// exporta ni se pasa por parametro: hoy hay uno. `GET /workspaces/` existe para
// el dia que haya subcuentas, y ese dia esto se convierte en un argumento; hacer
// hoy ese argumento seria escribir la parte dificil sin poder probarla.
const ESPACIO_ACTUAL = 'actual'

export interface Harness {
  workspace: { nombre: string; cuenta: string; desde: string }
  /** Que decide cual de dos trozos gana, en palabras. **Sin orden entre tipos**
   *  desde D6: el tipo de un documento dejo de decidir nada, asi que un orden
   *  entre tipos no ordenaba ya nada. */
  prioridad: { claves: string[] }
  exclusiones: { cuantas: number; excepcion: string }
  si_falta: Record<string, string | string[]>
  pendientes: { cuantas: number; desde: string | null }
  no_preguntar_de: { tema: string; por: string; desde: string }[]
  huella: string
}

/**
 * Una linea de «Lo que sabe tu agente» (4.1, D7).
 *
 * **Todo lo que se pinta viene escrito del servidor**, incluida la frase de
 * procedencia y el nombre del tipo. Aqui no se traduce ni se compone nada: las
 * cinco frases viven en `workspaces/memory_types.py` y escribirlas tambien
 * aqui seria tenerlas dos veces y que un dia dejen de coincidir.
 */
export interface WhatTheAgentKnows {
  id: string
  /** Lo que responde. Es la misma linea que viaja al agente en cada turno. */
  linea: string
  /** La clave interna. **No se pinta**: esta para agrupar y para llamar. */
  tipo: string
  /** El nombre del tipo tal como lo lee el cliente. */
  seLlama: string
  /** «Se lo dijiste tú», «Lo sacó de un archivo que subiste»… */
  procedencia: string
  /** `tu`, `el agente` o `un archivo`. Es por lo que se filtra. */
  quienLoDijo: string
  cuando: string
}

/** Por donde se puede filtrar. Son de donde salio, **no el tipo**: el cliente
 *  no elige tipos en ninguna parte, y ofrecerle filtrar por algo que no elige
 *  seria enseñarle el motor. */
export const WHO_SAID_IT = ['tu', 'el agente', 'un archivo'] as const
export type WhoSaidIt = (typeof WHO_SAID_IT)[number]

/**
 * Lo que el sistema propone al leer una frase (4.2, capacidad 1).
 *
 * **El tipo lo propone el servidor y el cliente nunca lo elige.** Viene con su
 * nombre en palabras (`seLlama`) y esa es la unica forma en la que se enseña:
 * la clave viaja para poder guardar, no para pintarla.
 */
export interface MemoriaPropuesta {
  tipo: string
  seLlama: string
  tema: string
  /** La linea que el agente leera. Es la que el cliente puede corregir. */
  linea: string
  revisarEn: string | null
  persona: string
  /** Lo que falta para poder guardarla, en palabras. Vacio si no falta nada. */
  falta: string
  sePuedeGuardar: boolean
}

/**
 * Una cosa esperando en la puerta (4.3, D8).
 *
 * **Una sola puerta para todo lo que el sistema propone**: memorias, la
 * referencia de un archivo recien subido, y las propuestas de organizacion que
 * se quedaron sin sitio al quitar el buzon. Tres tablas distintas y una sola
 * pregunta, porque quien decide no tiene por que saber en que tabla vive lo
 * que esta aprobando.
 */
export interface Pendiente {
  /** En que tabla vive: `anotacion`, `regla` u `organizacion`. Viaja para
   *  poder resolverla, no para pintarla. */
  que: string
  id: string
  /** La frase que se lee y se aprueba, **cuando es una frase**.
   *
   *  Una propuesta de organizar archivos manda aqui un objeto -a quien se le
   *  asigna, con que se relaciona-, no un texto. Decia `string` a secas y era
   *  mentira: nada lo comprobaba, y pintarlo de titular rompe la pantalla
   *  entera. Lo que se lee de esas sale del `motivo`. */
  propone: string | Record<string, unknown>
  /** Como se llama su tipo, cuando lo tiene. */
  seLlama?: string
  /** De que archivo salio, si salio de uno. Para leerlo. */
  archivo?: string | null
  /** El mismo archivo, por su identificador. Para agrupar lo que ha dado de si
   *  una subida (4.6): dos archivos pueden llamarse igual. */
  documento?: string | null
  /** **Que pasara si dices que si.** El motivo explica por que se propone;
   *  esto dice la consecuencia, que es lo que hay que aprobar. */
  efecto?: string | null
  motivo: string
  propuestaPor: string
  propuestaEn: string
}

/** «Tu negocio, en una pagina» (2.6, D4): lo que el agente lee entero antes
 *  que nada, con su tope de palabras. */
export interface ContextoDelNegocio {
  texto: string
  palabras: number
  tope: number
  escrito: boolean
}

export interface Elemento {
  id: number
  titulo: string
  /** Lo que el agente sabe de este archivo sin abrirlo (4.5, D11). `null`
   *  cuando nadie ha escrito todavia que es: entonces esta esperando en la
   *  puerta y quien lo dice es la puerta, no esto. */
  referencia: { id: string; linea: string; aceptada: boolean } | null
  utilizable: boolean
  motivo: string
}

const base = () => `/api/agent/v1/workspaces/${ESPACIO_ACTUAL}`

export const workspacesService = {
  async harness(): Promise<Harness> {
    return unwrap<Harness>(await ragApi.get(`${base()}/harness/`))
  },

  // Aqui vivian cuatro mas -ver el buzon, aprobar o rechazar, listar lo
  // anotado y retirar una anotacion-. Se fueron con la seccion del Workspace
  // el 2026-09-18: eran su unico llamador, y una llamada que nadie hace no es
  // una capacidad, es una promesa. Las operaciones siguen en la API, y lo que
  // tiene puerta hoy y lo que no esta escrito en `harness-secciones.json`.

  /**
   * Todo lo que sabe el agente, por fecha y sin agrupar.
   *
   * Mirar esta lista **no cuenta como usar una memoria**: la marca de uso la
   * mueve el agente al leerlas, no el dueño al revisarlas.
   */
  async loQueSabe(
    quien?: WhoSaidIt,
  ): Promise<{
    memorias: WhatTheAgentKnows[]
    cuantas: number
    noListadas: number
    /** Cuantas hay de cada origen, contadas **antes** de filtrar. */
    porQuien: Record<WhoSaidIt, number>
  }> {
    const datos = unwrap<{
      memorias: {
        id: string
        linea: string
        tipo: string
        se_llama: string
        procedencia: string
        quien_lo_dijo: string
        cuando: string
      }[]
      cuantas: number
      no_listadas: number
      por_quien: Record<WhoSaidIt, number>
    }>(
      // **Por `params`, no pegado a la ruta.** El cliente le añade una barra
      // final a lo que recibe, y con la consulta dentro esa barra acababa en
      // el valor: `?quien=tu/` no es ningun origen, asi que el servidor
      // contestaba una lista vacia mientras el recuento seguia diciendo 2.
      await ragApi.get(`${base()}/lo-que-sabe/`, {
        params: quien ? { quien } : undefined,
      }),
    )
    return {
      memorias: (datos.memorias ?? []).map((fila) => ({
        id: fila.id,
        linea: fila.linea,
        tipo: fila.tipo,
        seLlama: fila.se_llama,
        procedencia: fila.procedencia,
        quienLoDijo: fila.quien_lo_dijo,
        cuando: fila.cuando,
      })),
      cuantas: datos.cuantas ?? 0,
      noListadas: datos.no_listadas ?? 0,
      porQuien: datos.por_quien ?? { tu: 0, 'el agente': 0, 'un archivo': 0 },
    }
  },

  /**
   * De una frase a una memoria propuesta. **No guarda nada.**
   *
   * Devuelve `null` cuando no hay nada que recordar en la frase, con lo que
   * hay que contestarle en `queDecir`. No es un error: abstenerse es mejor que
   * forzar una memoria, porque una mala se sufre en cada conversacion.
   */
  async proponerMemoria(
    frase: string,
  ): Promise<{ propuesta: MemoriaPropuesta | null; queDecir: string }> {
    const datos = unwrap<{
      propuesta: {
        tipo: string
        se_llama: string
        tema: string
        resumen: string
        revisar_en: string | null
        persona: string
        falta: string
        se_puede_guardar: boolean
      } | null
      que_decir?: string
    }>(await ragApi.post(`${base()}/memory/proponer/`, { frase }))

    const cruda = datos.propuesta
    return {
      propuesta: cruda && {
        tipo: cruda.tipo,
        seLlama: cruda.se_llama,
        tema: cruda.tema,
        linea: cruda.resumen,
        revisarEn: cruda.revisar_en,
        persona: cruda.persona,
        falta: cruda.falta,
        sePuedeGuardar: cruda.se_puede_guardar,
      },
      queDecir: datos.que_decir ?? '',
    }
  },

  /**
   * Guarda lo que el cliente confirmo, con el tipo y la linea de la propuesta.
   *
   * La puerta es siempre `persona`: esto lo llama la pantalla, y quien esta en
   * la pantalla es una persona. Sin pasar `tipo` y `linea` se guardaria una
   * correccion con la primera linea del contenido, que no es lo que se
   * propuso.
   */
  async guardarMemoria(entrada: {
    propuesta: MemoriaPropuesta
    linea: string
    quienLaEscribio: string
  }): Promise<{ id: string; estado: string }> {
    return unwrap(
      await ragApi.post(`${base()}/memory/`, {
        tema: entrada.propuesta.tema,
        anotacion: entrada.linea,
        resumen: entrada.linea,
        tipo: entrada.propuesta.tipo,
        revisar_en: entrada.propuesta.revisarEn,
        persona: entrada.propuesta.persona,
        quien_la_escribio: entrada.quienLaEscribio,
        de_donde_salio: 'Enseñarle',
        puerta: 'persona',
      }),
    )
  },

  /** Todo lo que espera un si o un no, de las tres familias a la vez. */
  async pendientes(): Promise<Pendiente[]> {
    const datos = unwrap<{
      esperando: {
        que: string
        id: string
        propone: unknown
        se_llama?: string
        archivo?: string | null
        documento?: string | null
        efecto?: string | null
        motivo: string
        propuesta_por: string
        propuesta_en: string
      }[]
    }>(await ragApi.get(`${base()}/pendientes/`))

    return (datos.esperando ?? []).map((fila) => ({
      que: fila.que,
      id: fila.id,
      // Una propuesta de organizacion trae un objeto en `propone`; las de
      // memoria, una frase. Lo que se lee en la puerta es siempre el `efecto`
      // cuando lo hay, asi que aqui basta con no romperse.
      propone: typeof fila.propone === 'string' ? fila.propone : '',
      seLlama: fila.se_llama,
      archivo: fila.archivo ?? null,
      documento: fila.documento ?? null,
      efecto: fila.efecto ?? null,
      motivo: fila.motivo,
      propuestaPor: fila.propuesta_por,
      propuestaEn: fila.propuesta_en,
    }))
  },

  /**
   * Un si o un no. **El no pide motivo y el servidor lo exige**: un rechazo
   * sin motivo hace que se vuelva a proponer lo mismo mañana sin que nadie
   * recuerde que ya se dijo que no.
   */
  async resolverPendiente(entrada: {
    que: string
    id: string
    decision: 'aprobar' | 'rechazar'
    por: string
    motivo?: string
  }): Promise<{ id: string; estado: string }> {
    return unwrap(await ragApi.post(`${base()}/pendientes/`, entrada))
  },

  /**
   * Corregir el texto de una memoria. **Por identificador y no por nombre**:
   * encontrar mal al leer cuesta una respuesta rara, encontrar mal al
   * escribir pisa una memoria buena.
   */
  async corregirMemoria(id: string, linea: string): Promise<{ id: string; linea: string }> {
    return unwrap(await ragApi.post(`${base()}/memory/${id}/texto/`, { linea }))
  },

  /**
   * Retirar una memoria. **El motivo es obligatorio** y el servidor lo exige:
   * sin el, dentro de seis meses nadie sabe por que se fue, y el sistema puede
   * volver a proponer lo mismo.
   */
  async retirarMemoria(id: string, entrada: { por: string; motivo: string }) {
    return unwrap(await ragApi.post(`${base()}/memory/${id}/retirada/`, entrada))
  },

  async contexto(): Promise<ContextoDelNegocio> {
    return unwrap<ContextoDelNegocio>(await ragApi.get(`${base()}/contexto/`))
  },

  /**
   * Guardarlo. La puerta es siempre `persona`: esto lo llama la pantalla, y
   * quien esta en la pantalla es una persona. Pasado el tope el servidor no lo
   * guarda y dice cuanto sobra.
   */
  async guardarContexto(texto: string): Promise<ContextoDelNegocio> {
    return unwrap<ContextoDelNegocio>(
      await ragApi.put(`${base()}/contexto/`, { texto, puerta: 'persona' }),
    )
  },

  async elementos(): Promise<Elemento[]> {
    const datos = unwrap<{ items: Elemento[] }>(
      await ragApi.get(`${base()}/elementos/`),
    )
    return datos.items ?? []
  },

  async marcarUtilizable(
    elementoId: number,
    entrada: { utilizable: boolean; motivo?: string },
  ): Promise<{ id: number; utilizable: boolean; motivo: string }> {
    return unwrap(
      await ragApi.post(`${base()}/elementos/${elementoId}/exclusion/`, entrada),
    )
  },
}
